import type {ContainerQueryParams, SocialMediaWizardContainerQuery, SocialMediaWizardMediaTypeSelection} from '@Components/social-media-wizard/social-media-wizard.types';
import {useEffect, useRef} from 'react';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {containerQuery} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {GraphicsMetaMapType} from '@Components/social-media/publish-params.types';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import type {
  AccountNeedsResizeMap,
  AccountPublishableUrlForAllMediaTypeMap,
  AccountResizeStatusMap,
  CaptionErrorByAccountIds,
  DesignNeedsResizeInformation,
  DesignSelection,
  TitleErrorByAccountIds,
} from '@Components/social-media/social-media.types';
import {PosterResizeRequiredStatus} from '@Components/social-media/social-media.types';
import {
  useAccountIdsToPublishTo,
  usePublishingParams,
  usePublishingStatusForAllAccounts,
  useSavingPublishingParamsState,
  useSocialPostPublishingAction,
} from '@Components/social-media/social-media.hooks';
import {hasAnyPublishSucceeded, haveAllPublishingStatesSettled} from '@Libraries/social-media-wizard-accounts-library';
import type {SocialMediaPublishPhase} from '@Libraries/social-media-publish-phase.library';
import {getSocialMediaPublishPhase} from '@Libraries/social-media-publish-phase.library';
import {isEventOneClickPromotionFlow, isEventsSocialPostingFlow} from '@Libraries/social-media-event-promotion.library';
import {getAllDesignResizeStatuses} from '@Libraries/social-media-resize.library';
import {ReviewDialog, showReviewToast} from '@Libraries/review-collection-library';
import type {LoadingStates} from '@Utils/loading.util';
import type {MediaTypePublishingParams, SocialMediaAccountId} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {AccountResizeStatus} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import type {ErrorBoxInputs} from '@Components/error-box';
import type {DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import {useAppSelector} from '@/hooks';

export const useSocialMediaWizardContainerQuery = (): ContainerQueryParams => {
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);

  const containerQueryParamProps: SocialMediaWizardContainerQuery = {
    isExtraSmallScreen: params.xsmall,
    isSmallScreen: params.small,
    isMediumScreen: params.medium,
    isLargeScreen: params.large,
    isExtraLargeScreen: params.xlarge,
  };

  return {
    containerQueryParamProps,
    containerRef,
    hasContainerQueryInitialized: params.small !== undefined,
  };
};

export const useHasSeenConfetti = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.hasSeenConfetti;
  });
};

export const usePlatformSelectionforAccount = (accountId: number): SocialMediaWizardMediaTypeSelection => {
  return useAppSelector((state): SocialMediaWizardMediaTypeSelection => {
    return state.socialMediaWizard.platformSelectionForAccount[accountId];
  });
};

export const useIsTemplateSelected = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.isTemplateSelected;
  });
};

export const useIsSocialMediaWizardAiCaptionsEnabled = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.isSocialMediaWizardAiCaptionsEnabled;
  });
};

export const useIsGoogleBusinessProfileEnabled = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.isGoogleBusinessProfileEnabled;
  });
};

export const useAreAccountsLoading = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.areAccountsLoading;
  });
};

export const useHideNextButton = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.hideNextButton;
  });
};

export const useNumParentAccountsConnected = (): number => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.numParentAccountsConnected;
  });
};

export const useWizardCaptionErrors = (): CaptionErrorByAccountIds => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.captionError;
  });
};

export const useWizardTitleErrors = (): TitleErrorByAccountIds => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.titleError;
  });
};

export const useWizardConnectedAccounts = (): SocialAccount[] => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accounts;
  });
};

export const useWizardAccountsToPublishTo = (): number[] => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accountIdsToPublishTo;
  });
};

export const useDesignsToPublish = (): DesignSelection[] => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.designsToPublish;
  });
};

export const useWizardPublishingParams = (): Record<SocialMediaAccountId, MediaTypePublishingParams> => {
  return useAppSelector((state): Record<SocialMediaAccountId, MediaTypePublishingParams> => {
    return state.socialMediaWizard.publishingParams;
  });
};

export const useWizardDesignsGraphicsMetaMap = (): GraphicsMetaMapType => {
  return useAppSelector((state): GraphicsMetaMapType => {
    return state.socialMediaWizard.designsGraphicsMetaMap;
  });
};

export const useNeedsResizeMap = (): AccountNeedsResizeMap => {
  return useAppSelector((state): AccountNeedsResizeMap => {
    return state.socialMediaWizard.accountNeedsResizeMap;
  });
};

export const useAccountNeedsResizeMap = (accountId: SocialMediaAccountId | undefined): DesignNeedsResizeInformation | null => {
  return useAppSelector((state): DesignNeedsResizeInformation | null => {
    if (!accountId || !state.socialMediaWizard.accountNeedsResizeMap?.[accountId]) {
      return null;
    }

    return state.socialMediaWizard.accountNeedsResizeMap[accountId];
  });
};

export const useWizardDesignsGraphicMetaMap = (): GraphicsMetaMapType => {
  return useAppSelector((state): GraphicsMetaMapType => {
    return state.socialMediaWizard.designsGraphicsMetaMap;
  });
};

export const useAccountResizeStatusMap = (accountId: SocialMediaAccountId, platformMediaType: PlatformMediaType): AccountResizeStatusMap | null => {
  return useAppSelector((state): AccountResizeStatusMap | null => {
    return state.socialMediaWizard.accountResizeStatusMapForMediaType[accountId]?.[platformMediaType] ?? null;
  });
};

export const useIsPublishingVideoForAccount = (): boolean => {
  return useAppSelector((state): boolean => {
    const designsGraphicsMetaMap = state.socialMediaWizard.designsGraphicsMetaMap;

    if (!designsGraphicsMetaMap) {
      return false;
    }

    return Object.values(designsGraphicsMetaMap).some((pageGraphicMeta) => {
      return Object.values(pageGraphicMeta).some((graphicMeta) => {
        return graphicMeta.isVideo;
      });
    });
  });
};

export const useDesignSelectorTab = (): DesignSelectorTab => {
  return useAppSelector((state): DesignSelectorTab => {
    return state.socialMediaWizard.designSelectorTab;
  });
};

export const usePublishableURLsForAccount = (): AccountPublishableUrlForAllMediaTypeMap => {
  return useAppSelector((state): AccountPublishableUrlForAllMediaTypeMap => {
    return state.socialMediaWizard.publishableGraphicUrlForAllMediaTypeMap;
  });
};

export const useDesignsGraphicsMetaMapForAccountWithMediaType = (accountId: SocialMediaAccountId, platformMediaType: PlatformMediaType): GraphicsMetaMapType => {
  return useAppSelector((state): GraphicsMetaMapType => {
    const mediaTypePublishingParams = state.socialMediaWizard.publishingParams[accountId];

    const publishingParams = mediaTypePublishingParams[platformMediaType];

    if (!publishingParams?.graphicsMetaMap) {
      return state.socialMediaWizard.designsGraphicsMetaMap;
    }

    return publishingParams.graphicsMetaMap;
  });
};

export const useIsAnyPosterResizingForAccountWithMediaType = (accountId: SocialMediaAccountId, platformMediaType: PlatformMediaType): boolean => {
  return useAppSelector((state): boolean => {
    const resizingStatusForPosters = state.socialMediaWizard.accountResizeStatusMapForMediaType[accountId]?.[platformMediaType];

    if (!resizingStatusForPosters) {
      return false;
    }

    return getAllDesignResizeStatuses(resizingStatusForPosters).includes(AccountResizeStatus.RESIZING);
  });
};

export const useIsAnyPosterResizingForAccount = (accountId: SocialMediaAccountId): boolean => {
  return useAppSelector((state): boolean => {
    const resizingStatusForPosters = state.socialMediaWizard.accountResizeStatusMapForMediaType[accountId];

    if (!resizingStatusForPosters) {
      return false;
    }

    const platformMediaTypes: PlatformMediaType[] = [PlatformMediaType.POST, PlatformMediaType.STORIES, PlatformMediaType.REELS];

    return platformMediaTypes.some((platformMediaType) => {
      const resizingPosterStatusForMediaType = resizingStatusForPosters[platformMediaType];

      if (!resizingPosterStatusForMediaType) {
        return false;
      }

      return getAllDesignResizeStatuses(resizingPosterStatusForMediaType).includes(AccountResizeStatus.RESIZING);
    });
  });
};

const isResizeRequiredForPoster = (accountResizeRequiredStatus: PosterResizeRequiredStatus): boolean => {
  return accountResizeRequiredStatus === PosterResizeRequiredStatus.RESIZE_NEEDED;
};

export const useDoesAnyPosterRequireResizeForAccount = (accountId: SocialMediaAccountId | undefined, platformMediaType: PlatformMediaType): boolean => {
  return useAppSelector((state): boolean => {
    if (!accountId) {
      return false;
    }

    const resizeRequiredForAccount = state.socialMediaWizard.accountNeedsResizeMap[accountId];
    const publishingParams = state.socialMediaWizard.publishingParams[accountId];
    const {accounts} = state.socialMediaWizard;

    if (!publishingParams) {
      return false;
    }

    const specificAccount = accounts.find((account) => {
      return account.id === accountId;
    });

    if (!specificAccount) {
      console.error('No account with id: ', accountId, ' found');
      return false;
    }

    if (!resizeRequiredForAccount) {
      return false;
    }

    return Object.values(resizeRequiredForAccount).some((pageResizeInformation) => {
      return Object.values(pageResizeInformation).some((resizeStatus) => {
        if (!resizeStatus) {
          console.error('No resize information found for poster');
          return false;
        }

        const resizeRequiredStatus = {
          [PlatformMediaType.POST]: resizeStatus.needsResizing,
          [PlatformMediaType.REELS]: resizeStatus.reelNeedsResizing,
          [PlatformMediaType.STORIES]: resizeStatus.storyNeedsResizing,
        }[platformMediaType];

        return !!(resizeRequiredStatus && isResizeRequiredForPoster(resizeRequiredStatus));
      });
    });
  });
};

export const useHasAnyPosterResizeErroredForAccount = (accountId: SocialMediaAccountId, platformMediaType: PlatformMediaType | undefined): boolean => {
  return useAppSelector((state): boolean => {
    const resizingStatusForPosters = state.socialMediaWizard.accountResizeStatusMapForMediaType[accountId]?.[platformMediaType ?? PlatformMediaType.POST];

    if (!resizingStatusForPosters) {
      return false;
    }

    return getAllDesignResizeStatuses(resizingStatusForPosters).includes(AccountResizeStatus.RESIZE_ERROR);
  });
};

export const useIsAnyPosterLoadingForAccount = (accountId: SocialMediaAccountId, platforMediaType: PlatformMediaType): boolean => {
  return useAppSelector((state): boolean => {
    const publishingParamsForAccount = state.socialMediaWizard.publishingParams[accountId];

    if (!publishingParamsForAccount) {
      return false;
    }

    const publishingParamsForPlatform = publishingParamsForAccount[platforMediaType];

    if (!publishingParamsForPlatform) {
      return false;
    }

    const designsGraphicMetaForAccount = publishingParamsForPlatform.graphicsMetaMap;

    if (!designsGraphicMetaForAccount) {
      console.error('No graphics meta found for account.');
      return false;
    }

    return Object.values(designsGraphicMetaForAccount).some((pageGraphicsMetaMap) => {
      return Object.values(pageGraphicsMetaMap).some((graphicMeta) => {
        return graphicMeta.graphicURL === '';
      });
    });
  });
};

export const useAccountLevelErrorMessage = (): ErrorBoxInputs => {
  return useAppSelector((state): ErrorBoxInputs => {
    return state.socialMediaWizard.accountLevelErrorMessage;
  });
};

export const usePostId = (): string | null => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.post?.id ?? null;
  });
};

export const useAreCaptionsBeingAutoGenerated = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.areCaptionsBeingAutoGenerated;
  });
};

export const useGraphicsReadiness = (): LoadingStates => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.graphicsReadiness.status;
  });
};

/** Single source of truth for publish progress, so every chrome surface freezes and unfreezes together. */
export const useSocialMediaPublishPhase = (): SocialMediaPublishPhase => {
  const publishingAction = useSocialPostPublishingAction();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();
  const savingPublishingParamsState = useSavingPublishingParamsState();

  return getSocialMediaPublishPhase({publishingAction, accountIdsToPublishTo, publishingStatusForAllAccounts, savingPublishingParamsState});
};

/**
 * Fires exactly once per publish/schedule when all account+media states settle with ≥1 success (failures
 * don't block it). Mount from a single-instance component (SocialMediaPostDetails), never per-account.
 */
export const useShowReviewRequestOnPublishSettle = (): void => {
  const publishingAction = useSocialPostPublishingAction();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();
  const publishingParams = usePublishingParams();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const hasFiredReviewRequestRef = useRef(false);

  useEffect(() => {
    if (publishingAction !== SocialPostPublishingAction.PUBLISH && publishingAction !== SocialPostPublishingAction.SCHEDULE) {
      return;
    }

    if (hasFiredReviewRequestRef.current || isEventsSocialPostingFlow() || isEventOneClickPromotionFlow()) {
      return;
    }

    if (
      !haveAllPublishingStatesSettled(accountIdsToPublishTo, publishingStatusForAllAccounts, publishingParams) ||
      !hasAnyPublishSucceeded(accountIdsToPublishTo, publishingStatusForAllAccounts, publishingParams)
    ) {
      return;
    }

    hasFiredReviewRequestRef.current = true;
    void showReviewToast(ReviewDialog.POST_PUBLISH_SOCIAL);
  }, [publishingAction, accountIdsToPublishTo, publishingStatusForAllAccounts, publishingParams]);
};
